import { ref } from 'vue'
import { useToastStore } from '@uiKit/store/toastStore'
import { useDiscardListingRequestDraftMutation } from '../api/useDiscardListingRequestDraftMutation'

/**
 * Discarding a draft from the Sell Requests list. Irreversible from the seller's
 * side, so it is two-step: `askToDiscard` opens the confirmation, `confirmDiscard`
 * deletes. The outcome is reported as a toast — it answers an action the seller
 * just took, and the row itself disappears on success.
 */
export function useDraftDiscard() {
  const toastStore = useToastStore()
  const discardDraftMutation = useDiscardListingRequestDraftMutation()
  const draftPendingDiscard = ref<string | null>(null)

  function askToDiscard(id: string) {
    draftPendingDiscard.value = id
  }

  function cancelDiscard() {
    draftPendingDiscard.value = null
  }

  async function confirmDiscard() {
    const id = draftPendingDiscard.value
    if (!id) return
    try {
      await discardDraftMutation.mutateAsync(id)
      toastStore.addToast({ type: 'success', message: 'Draft discarded.' })
    } catch {
      toastStore.addToast({ type: 'error', message: 'We couldn’t discard this draft. Try again.' })
    } finally {
      draftPendingDiscard.value = null
    }
  }

  return {
    draftPendingDiscard,
    isDiscarding: discardDraftMutation.isPending,
    askToDiscard,
    cancelDiscard,
    confirmDiscard,
  }
}
